import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import {
  errors,
  IFetchProcessesDto,
  IRydeProcess,
  isGlobalLoaderActiveSelector,
  ITranslatableItem,
  normalizeErrors,
  ProcessesApi,
  RydeUiUnit,
  warnAndToastException,
} from "@ryde/shared";

import {
  LayoutRightLoader,
  LayoutRightLoaderSlotStyled,
} from "../../../../right";
import { ProcessesGrid } from "./processes-grid";
import { ProcessesHistoryWrapper } from "./processes-history-wrapper";

interface IProcessesTabContentProps {
  id: number;
  entity: RydeUiUnit;
}

export const ProcessesTabContent: FC<IProcessesTabContentProps> = ({
  id,
  entity,
}) => {
  const { t } = useTranslation();
  const [processes, setProcesses] = useState<IRydeProcess[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ITranslatableItem | null>(null);
  const isGlobalLoaderActive = useRecoilValue(isGlobalLoaderActiveSelector);

  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dto: IFetchProcessesDto = {
        entityName: entity,
        affectedId: id,
      };
      const data = await ProcessesApi.fetchProcesses(dto);
      setProcesses(data);
    } catch (err) {
      const translatableError = warnAndToastException({
        error: err,
        fallbackTranslationKey: errors.processes.errorFetchingProcesses,
        translationFunction: t,
        logWarnMessage: "Failed to fetch processes",
      });
      setError(translatableError);
    } finally {
      setLoading(false);
    }
  }, [entity, id, t]);

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  return (
    <ProcessesHistoryWrapper>
      {error && !loading && (
        <ErrorsDisplayContainer
          marginLeft="22px"
          serverErrors={normalizeErrors(error, t)}
        />
      )}
      {!loading && <ProcessesGrid processes={processes} />}
      {loading && !isGlobalLoaderActive && (
        <LayoutRightLoaderSlotStyled>
          <LayoutRightLoader>{t("ui.LOADING")}...</LayoutRightLoader>
        </LayoutRightLoaderSlotStyled>
      )}
    </ProcessesHistoryWrapper>
  );
};
